import { useEffect, useRef, useState, useCallback } from "react";
import { useGeoPi } from "@/context/GeoPiContext";
import { GeoPiMark } from "@/components/GeoPiLogo";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import {
  X, Plus, History, Send, Paperclip, Copy, RefreshCw, ThumbsUp, ThumbsDown, FileDown, FilePlus, Loader2, Download,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const DOC_RE = /\[\[GEOPI_DOC:([^|]+)\|([^|]+)\|([^\]]+)\]\]/;
const GREETING = "Hi, I'm GeoPi. Ask me anything, attach data to analyse, or ask for a report section.";
const PILLS = [
  "Explain IP chargeability anomalies in gold exploration",
  "Suggest a soil sampling grid for a 4 km² licence",
  "What processing should I run on magnetic data before interpretation?",
  "Draft an executive summary outline for an exploration report",
];
const newId = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

export default function GeoPiPanel() {
  const { open, setOpen, project } = useGeoPi();
  const [sessionId, setSessionId] = useState(newId);
  const [messages, setMessages] = useState([]); // {id, role, content}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [feedback, setFeedback] = useState({});
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const lastUserRef = useRef("");
  const greeting = project?.name
    ? `I have loaded ${project.name} context. Ask me anything about this project.`
    : GREETING;

  const scrollDown = () => setTimeout(() => scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }), 50);
  useEffect(() => { if (open) scrollDown(); }, [open, messages]);
  const projRef = useRef(null);
  useEffect(() => {
    if (open && project?.id && projRef.current !== project.id) {
      projRef.current = project.id;
      setSessionId(newId()); setMessages([]); setAttachment(null);
    }
  }, [open, project]);

  const loadSessions = useCallback(() => {
    fetch(`${API}/api/geopi/sessions`).then((r) => r.json()).then(setSessions).catch(() => {});
  }, []);

  const newChat = () => { setSessionId(newId()); setMessages([]); setAttachment(null); setShowHistory(false); };

  const openSession = async (sid) => {
    try {
      const r = await fetch(`${API}/api/geopi/sessions/${sid}`);
      const data = await r.json();
      setSessionId(sid);
      setMessages(data.map((m) => ({ id: newId(), role: m.role, content: m.content })));
      setShowHistory(false); scrollDown();
    } catch { toast.error("Could not load that chat"); }
  };

  const stream = async ({ message, regenerate = false, attach = null }) => {
    setBusy(true);
    const aId = newId();
    setMessages((m) => [...m, { id: aId, role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/api/geopi/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message, attachment: attach, regenerate, project_context: project?.context || "" }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        setMessages((m) => m.map((x) => (x.id === aId ? { ...x, content: x.content + chunk } : x)));
      }
    } catch {
      setMessages((m) => m.map((x) => (x.id === aId ? { ...x, content: x.content || "GeoPi is unavailable right now. Please try again." } : x)));
    } finally { setBusy(false); loadSessions(); scrollDown(); }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if ((!msg && !attachment) || busy) return;
    lastUserRef.current = msg;
    const label = (attachment ? `📎 ${attachment.name}\n` : "") + msg;
    setMessages((m) => [...m, { id: newId(), role: "user", content: label }]);
    const attach = attachment; setAttachment(null); setInput("");
    await stream({ message: msg, attach });
  };

  const regenerate = async () => {
    if (busy || !lastUserRef.current) return;
    setMessages((m) => { const c = [...m]; if (c[c.length - 1]?.role === "assistant") c.pop(); return c; });
    await stream({ message: lastUserRef.current, regenerate: true });
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      toast.loading("Reading file…", { id: "gpf" });
      const r = await fetch(`${API}/api/geopi/upload`, { method: "POST", body: fd });
      const d = await r.json();
      setAttachment({ name: d.name, content: d.content });
      toast.success(`Attached ${d.name}`, { id: "gpf" });
    } catch { toast.error("Upload failed", { id: "gpf" }); }
    e.target.value = "";
  };

  const copyMsg = (c) => { navigator.clipboard.writeText(c); toast.success("Copied as report text"); };
  const insertReport = async (content) => {
    if (!project?.id) return;
    try {
      const r = await fetch(`${API}/api/projects/${project.id}/geopi-sections`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ heading: "GeoPi Analysis", content }),
      });
      if (!r.ok) throw new Error("insert failed");
      try { window.dispatchEvent(new CustomEvent("geopi:section-inserted", { detail: { pid: project.id } })); } catch (_) { /* noop */ }
      toast.success(`Added to ${project.name} report under "GeoPi Analysis"`);
    } catch { toast.error("Could not insert into report"); }
  };
  const exportPdf = (id) => {
    const el = document.getElementById(`gp-${id}`); if (!el) return;
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>GeoPi</title><style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#1e293b;max-width:780px;margin:40px auto;padding:0 24px;line-height:1.6}h1,h2,h3{color:#0f172a}table{border-collapse:collapse;width:100%}th,td{border:1px solid #cbd5e1;padding:6px 10px}code{background:#f1f5f9;padding:1px 5px;border-radius:4px}pre{background:#f1f5f9;padding:12px;border-radius:8px;overflow:auto}</style></head><body><h2>GeoPi — Geoscience Assistant</h2>${el.innerHTML}<hr/><p style="color:#94a3b8;font-size:12px">Generated by GeoPi · SYMIN · Shanvi Resources</p></body></html>`);
    w.document.close(); setTimeout(() => w.print(), 300);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[3000] flex justify-end" data-testid="geopi-panel">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full sm:w-[440px] h-full bg-[#0b1220] border-l border-slate-800 flex flex-col animate-slide-in-right shadow-2xl">
        {/* header */}
        <div className="flex items-center justify-between px-4 h-16 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="relative"><GeoPiMark size={30} /><span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 border-2 border-[#0b1220]" /></div>
            <div className="min-w-0">
              <div className="font-display font-extrabold text-white leading-none text-[15px]">GeoP<span className="relative">ı<span className="absolute left-1/2 -translate-x-1/2 -top-[3px] h-[4px] w-[4px] rounded-full bg-amber-400" /></span></div>
              <div className="text-[10px] text-slate-400 mt-0.5">Intelligent Geoscience Assistant</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => { setShowHistory((s) => !s); loadSessions(); }} data-testid="geopi-history" title="History" className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"><History size={17} /></button>
            <button onClick={newChat} data-testid="geopi-new" title="New chat" className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"><Plus size={18} /></button>
            <button onClick={() => setOpen(false)} data-testid="geopi-close" title="Close" className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"><X size={18} /></button>
          </div>
        </div>

        {/* history drawer */}
        {showHistory && (
          <div className="absolute top-16 right-0 w-full sm:w-72 max-h-[60%] overflow-auto bg-[#0f172a] border-l border-b border-slate-800 z-10 p-2" data-testid="geopi-history-list">
            {sessions.length === 0 && <div className="text-slate-500 text-xs p-4 text-center">No past conversations yet.</div>}
            {sessions.map((s) => (
              <button key={s.session_id} onClick={() => openSession(s.session_id)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-800 group">
                <div className="text-sm text-slate-200 truncate group-hover:text-white">{s.title}</div>
                <div className="text-[10px] text-slate-500">{s.updated_at ? new Date(s.updated_at).toLocaleString() : ""}</div>
              </button>
            ))}
          </div>
        )}

        {/* messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
          <div className="flex gap-2.5">
            <GeoPiMark size={24} className="shrink-0 mt-0.5" />
            <div className="rounded-2xl rounded-tl-sm bg-slate-800/70 text-slate-200 text-sm px-4 py-3 leading-relaxed">{greeting}</div>
          </div>
          {messages.map((m) => (
            m.role === "user" ? (
              <div key={m.id} className="flex justify-end">
                <div className="rounded-2xl rounded-tr-sm bg-teal-600 text-white text-sm px-4 py-2.5 max-w-[85%] whitespace-pre-wrap">{m.content}</div>
              </div>
            ) : (
              <div key={m.id} className="flex gap-2.5">
                <GeoPiMark size={24} className="shrink-0 mt-0.5" />
                <div className="max-w-[88%]">
                  <div id={`gp-${m.id}`} className="geopi-md rounded-2xl rounded-tl-sm bg-slate-800/70 px-4 py-3">
                    {m.content ? (() => {
                      const dm = m.content.match(DOC_RE);
                      const clean = m.content.replace(DOC_RE, "").trim();
                      return (
                        <>
                          {clean && <ReactMarkdown remarkPlugins={[remarkGfm]}>{clean}</ReactMarkdown>}
                          {dm && (
                            <a href={`${API}/api/geopi/documents/${dm[1]}`} download data-testid="geopi-doc-download"
                              className="mt-2 inline-flex items-center gap-2 rounded-xl bg-teal-500 hover:bg-teal-400 text-white text-xs font-semibold px-3.5 py-2 transition-colors no-underline">
                              <Download size={14} /> Download {dm[3]}
                            </a>
                          )}
                        </>
                      );
                    })() : <Loader2 size={16} className="animate-spin text-slate-400" />}
                  </div>
                  {m.content && !busy && (
                    <div className="flex items-center gap-1 mt-1.5 pl-1">
                      <button onClick={() => copyMsg(m.content)} title="Copy" className="h-7 w-7 grid place-items-center rounded-md text-slate-500 hover:text-white hover:bg-slate-800"><Copy size={14} /></button>
                      <button onClick={regenerate} title="Regenerate" className="h-7 w-7 grid place-items-center rounded-md text-slate-500 hover:text-white hover:bg-slate-800"><RefreshCw size={14} /></button>
                      <button onClick={() => setFeedback((f) => ({ ...f, [m.id]: f[m.id] === "up" ? null : "up" }))} title="Good" className={`h-7 w-7 grid place-items-center rounded-md hover:bg-slate-800 ${feedback[m.id] === "up" ? "text-emerald-400" : "text-slate-500 hover:text-white"}`}><ThumbsUp size={14} /></button>
                      <button onClick={() => setFeedback((f) => ({ ...f, [m.id]: f[m.id] === "down" ? null : "down" }))} title="Needs work" className={`h-7 w-7 grid place-items-center rounded-md hover:bg-slate-800 ${feedback[m.id] === "down" ? "text-red-400" : "text-slate-500 hover:text-white"}`}><ThumbsDown size={14} /></button>
                      <button onClick={() => exportPdf(m.id)} title="Export as PDF" className="h-7 w-7 grid place-items-center rounded-md text-slate-500 hover:text-white hover:bg-slate-800"><FileDown size={14} /></button>
                      {project?.id && (
                        <button onClick={() => insertReport(m.content)} data-testid="geopi-insert-report" title="Insert into report" className="h-7 w-7 grid place-items-center rounded-md text-slate-500 hover:text-teal-300 hover:bg-slate-800"><FilePlus size={14} /></button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          ))}
          {messages.length === 0 && (
            <div className="grid gap-2 pt-1">
              {PILLS.map((p) => (
                <button key={p} onClick={() => send(p)} data-testid="geopi-pill"
                  className="text-left text-[13px] text-slate-300 border border-slate-700 hover:border-teal-500 hover:bg-slate-800/60 rounded-xl px-3.5 py-2.5 transition-colors">{p}</button>
              ))}
            </div>
          )}
        </div>

        {/* input bar */}
        <div className="border-t border-slate-800 p-3 shrink-0">
          {attachment && (
            <div className="flex items-center gap-2 mb-2 text-xs text-slate-300 bg-slate-800 rounded-lg px-3 py-1.5 w-fit">
              <Paperclip size={12} /> {attachment.name}
              <button onClick={() => setAttachment(null)} className="text-slate-500 hover:text-white"><X size={12} /></button>
            </div>
          )}
          <div className="flex items-end gap-2 bg-slate-800/70 rounded-2xl px-2 py-1.5">
            <input ref={fileRef} type="file" className="hidden" onChange={onFile}
              accept=".csv,.txt,.json,.md,.geojson,.tsv,.xml,.xlsx,.xls,.pdf,.png,.jpg,.jpeg,.zip,.shp" data-testid="geopi-file" />
            <button onClick={() => fileRef.current?.click()} title="Attach data" className="h-9 w-9 grid place-items-center rounded-lg text-slate-400 hover:text-white shrink-0"><Paperclip size={18} /></button>
            <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1} data-testid="geopi-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask GeoPi…" className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 resize-none outline-none py-1.5 max-h-28" />
            <button onClick={() => send()} disabled={busy} data-testid="geopi-send"
              className="h-9 w-9 grid place-items-center rounded-xl bg-teal-500 hover:bg-teal-400 disabled:opacity-40 text-white shrink-0">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
